import type { FunctionComponent } from 'react'
import React from 'react'
import { Select, Input } from '@arvoreeducacao/bonsai-ds'
import { useUserActionsModal } from './UserActions.hooks'
import { Modal, RightButton, Text, Wrapper } from './UserActions.styles'
import { ModalTypes, UserTypes, userClassifications } from '../constants'

import type { LanguageTokens } from 'i18n'
import { useTranslation } from 'i18n'
import type { UserActionsModalProps } from './types'
import usePermissions from 'utils/hooks/use-permissions'
import { useUserInfo } from 'utils/hooks/use-userInfo'

const UserActionsModal: FunctionComponent<UserActionsModalProps> = ({
  type,
  client,
  entityId,
  userName,
  userId,
  userEmail,
  userAccessToken,
  readerId,
  isOpen,
  onClose,
  userIsAdmin,
  editorCanSeeUserToken
}) => {
  const { t } = useTranslation()

  const userType = userIsAdmin ? 'admin' : 'reader'
  const { canManageUsers } = usePermissions()
  const { user } = useUserInfo()

  const newPermissionsManagement =
    user?.featureToggles?.newPermissionsManagement

  const {
    modalTitle,
    buttonLabel,
    selectorItems,
    canSubmitEntity,
    invalidEmail,
    handleSubmit,
    handleUserName,
    handleUserType,
    handleUserEmail,
    handleEntityId,
    editedEntityId
  } = useUserActionsModal({
    userType,
    userName,
    userId,
    userEmail,
    type,
    client,
    entityId,
    readerId,
    canManageUsers
  })
  return (
    <Modal
      data-testid={`user-modal-${type}`}
      show={isOpen}
      canCloseByXBtn
      title={t(modalTitle as LanguageTokens)}
      onClose={onClose}
    >
      {[ModalTypes.edit, ModalTypes.create].includes(type) ? (
        <Wrapper>
          {selectorItems && (
            <Select
              data-testid={'user-level-selector'}
              placeholder={t('Tipo de usuário')}
              items={selectorItems.map(item => ({
                ...item,
                text: t(item.text as LanguageTokens)
              }))}
              disabled={
                ModalTypes.edit === type ||
                (!canManageUsers && newPermissionsManagement)
              }
              defaultOption={
                ModalTypes.edit === type
                  ? userClassifications.find(
                      d =>
                        d.id ===
                        (userIsAdmin ? UserTypes.admin : UserTypes.reader)
                    )
                  : undefined
              }
              onChange={item => handleUserType(item.id)}
            />
          )}
          {!entityId && type === ModalTypes.create && (
            <Input
              data-testid={'entity-id-input'}
              disabled={!canManageUsers && newPermissionsManagement}
              placeholder={t('Id da entidade')}
              value={editedEntityId}
              onChange={e => handleEntityId(e.target.value)}
              type="number"
            />
          )}
          <Input
            data-testid={'name-input'}
            disabled={!canManageUsers && newPermissionsManagement}
            placeholder={t('Nome')}
            defaultValue={userName}
            onChange={e => handleUserName(e.target.value)}
          />
          <Input
            data-testid={'email-input'}
            disabled={
              type === ModalTypes.edit ||
              (!canManageUsers && newPermissionsManagement)
            }
            placeholder={t('E-mail')}
            value={type === ModalTypes.edit ? userEmail : null}
            onChange={e => handleUserEmail(e.target.value)}
            error={invalidEmail}
            helpText={invalidEmail && t('Email inválido')}
          />
          {type === ModalTypes.edit && editorCanSeeUserToken && (
            <Input
              data-testid={'token-input'}
              disabled={true}
              type="password"
              placeholder={t('Token')}
              value={userAccessToken}
            />
          )}
        </Wrapper>
      ) : (
        <Text>
          {t('Você tem certeza que deseja remover o usuário')} <b>{userName}</b>
          ?
        </Text>
      )}
      <RightButton
        disabled={
          !canSubmitEntity ||
          (!canManageUsers &&
            newPermissionsManagement &&
            type !== ModalTypes.delete)
        }
        data-testid={'submit-button'}
        style={{ width: '100px' }}
        theme={{ type: 'primaryLivros', size: 'large' }}
        text={t(buttonLabel as LanguageTokens)}
        onClick={() => handleSubmit()}
      />
    </Modal>
  )
}

export default UserActionsModal
